import { useEffect, useState } from 'react'
import { ChevronRight, ExternalLink } from 'lucide-react'
import type { CatalogOption } from './types'
import type { ProfileEpisode } from './profileTypes'
import type { CatalogEntityPayload } from './catalogEntityTypes'
import { relationshipStatus } from './profileRelationshipStatus'

function dateLabel(value: string | null) {
  if (!value) return null
  const date = new Date(`${value.slice(0, 10)}T12:00:00`)
  return Number.isNaN(date.getTime()) ? null : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date)
}

export default function CatalogEntityContent({ item, onOpenPerson, onOpenEpisode, onClose }: {
  item: CatalogOption
  onOpenPerson: (personId: string, name: string) => void
  onOpenEpisode: (episode: ProfileEpisode) => void
  onClose: () => void
}) {
  const [record, setRecord] = useState<CatalogEntityPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    setRecord(null)
    setLoading(true)
    setError('')
    fetch(`/api/entities/${encodeURIComponent(item.id)}?type=${item.type}`, { signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error('This library page could not be loaded. Please reopen it to try again.')
        return response.json() as Promise<CatalogEntityPayload>
      })
      .then(setRecord)
      .catch((requestError: Error) => { if (requestError.name !== 'AbortError') setError(requestError.message) })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [item.id, item.type])

  if (loading) return <div className="feed-state"><span className="feed-spinner" />Loading this library page…</div>
  if (error || !record) return <p className="entity-description" role="alert">{error || 'This library page is unavailable.'}</p>
  const entity = record.entity
  const status = entity.type === 'relationship' ? relationshipStatus(entity.status, entity.status_review) : null
  const statusDate = entity.type === 'relationship' && status?.key !== 'unverified' ? dateLabel(entity.status_as_of) : null

  return (
    <div className="catalog-entity-content">
      {entity.type === 'relationship' ? (
        <>
          <div className="status-callout">
            <span>Relationship status</span><strong>{status?.label}</strong>
            {status?.key === 'unverified' ? <p>This relationship's current status is still under editorial review.</p> : (
              <p>{statusDate ? `Source dated ${statusDate}. ` : 'Published editorial assertion. '}
                {entity.source_url && <a href={entity.source_url} target="_blank" rel="noopener noreferrer">Read the relationship source <ExternalLink size={11} /></a>}
              </p>
            )}
          </div>
          <div className="entity-metrics"><div><strong>{entity.seasons.length}</strong><span>credited seasons</span></div><div><strong>{entity.members.length}</strong><span>linked cast</span></div><div><strong>{entity.seasons.reduce((count, season) => count + season.episodes.length, 0)}</strong><span>season-guide episodes</span></div></div>
          <h3>Connected cast</h3>
          <div className="cast-profile__connections">
            {entity.members.map(member => <button type="button" className="cast-profile__person-link" key={member.id} onClick={() => onOpenPerson(member.id, member.name)}><span className="cast-profile__person-avatar">{member.name.split(/\s+/).map(part => part[0]).slice(0, 2).join('')}</span><span><small>CAST PROFILE</small><strong>{member.name}</strong><em>Open cast profile</em></span><ChevronRight size={18} /></button>)}
          </div>
          <h3>Episodes in credited seasons</h3>
          <p className="entity-description">These are indexed episode guides for the couple's credited seasons, not verified individual episode appearances.</p>
          <div className="catalog-entity-seasons">
            {entity.seasons.map(season => <details key={`${season.show}:${season.season}`}>
              <summary>{season.show} · Season {season.season}<small>{season.episodes.length} indexed episodes</small></summary>
              <div className="cast-profile__episodes">{season.episodes.map(episode => <button type="button" key={episode.id} onClick={() => onOpenEpisode(episode)}><span className="cast-profile__episode-number">{episode.episode === null ? 'SP' : `E${episode.episode}`}</span><span><strong>{episode.title}</strong><em>{dateLabel(episode.air_date) || 'Air date unavailable'}</em></span><ChevronRight size={17} /></button>)}{season.episodes.length === 0 && <p className="cast-profile__episode-empty">No numbered episodes indexed for this season yet.</p>}</div>
            </details>)}
            {entity.seasons.length === 0 && <p className="entity-description">No season credits are indexed for this journey yet.</p>}
          </div>
        </>
      ) : (
        <div className="status-callout"><span>Indexed episode</span><strong>{entity.series} · Season {entity.season}{entity.episode === null ? ' · Special' : ` · Episode ${entity.episode}`}</strong><p>{dateLabel(entity.air_date) ? `Air date: ${dateLabel(entity.air_date)}.` : 'Air date unavailable.'}{entity.runtime_minutes ? ` ${entity.runtime_minutes} minutes.` : ''} {entity.source_url && <a href={entity.source_url} target="_blank" rel="noopener noreferrer">Episode source <ExternalLink size={11} /></a>}</p></div>
      )}

      <section className="catalog-entity-discussion" aria-label="Connected discussion">
        <h3>Connected discussion</h3>
        <p className="entity-description">Public posts tagged with this exact {entity.type === 'relationship' ? 'couple journey' : 'episode'}.</p>
        {record.posts.map(post => <article key={post.id}>
          <header><strong>{post.author.display_name.replace(/ · Demo( fan)?$/, '')}</strong><small>@{post.author.handle}</small></header>
          <p>{post.body}</p>
          <footer><span>{post.counts.comments} replies · {post.counts.reactions} reactions</span><a href={`#post-${post.id}`} onClick={onClose}>Open post and replies <ChevronRight size={13} /></a></footer>
        </article>)}
        {record.posts.length === 0 && <p className="entity-description">No public posts are tagged here yet. Use “Tag in a new post” to start the conversation.</p>}
        {record.discussion.has_more && <p className="entity-description">Showing the latest {record.discussion.limit} tagged public posts.</p>}
      </section>
    </div>
  )
}
